(() => {
  if (customElements.get('bought-together')) return;

  class BoughtTogether extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-bought-together-form]');
      this.items = Array.from(this.querySelectorAll('[data-bought-together-item]'));
      this.submitButton = this.querySelector('[data-bought-together-submit]');
      this.spinner = this.querySelector('[data-bought-together-spinner]');
      this.status = this.querySelector('[data-bought-together-status]');
      this.total = this.querySelector('[data-bought-together-total]');
      this.totalCompare = this.querySelector('[data-bought-together-total-compare]');
      this.totalBadge = this.querySelector('[data-bought-together-total-badge]');
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.loading = false;

      if (!this.form || this.items.length === 0) return;

      this.onClick = (event) => this.handleClick(event);
      this.onChange = (event) => this.handleChange(event);
      this.onInput = (event) => this.handleInput(event);
      this.onSubmit = (event) => this.handleSubmit(event);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.form.addEventListener('submit', this.onSubmit);

      this.items.forEach((item) => {
        const select = item.querySelector('[data-bought-together-variant]');
        if (select?.selectedOptions[0]) {
          this.applyVariant(item, select.selectedOptions[0], false);
        }
      });

      this.restoreState();

      this.items.forEach((item) => {
        const checkbox = item.querySelector('[data-bought-together-select]');
        const selected = checkbox ? checkbox.checked : true;
        this.setItemSelected(item, selected, false);

        const input = item.querySelector('[data-bought-together-quantity]');
        if (input && !input.disabled) this.normalizeQuantity(input);
      });

      this.updateTotals();
    }

    disconnectedCallback() {
      this.saveState();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    get stateKey() {
      return [
        this.dataset.sectionId || '',
        this.dataset.blockId || '',
        this.dataset.productHandle || '',
      ].join(':');
    }

    get stateStore() {
      window.__boughtTogetherState ||= new Map();
      return window.__boughtTogetherState;
    }

    restoreState() {
      const savedItems = this.stateStore.get(this.stateKey);
      if (!savedItems) return;

      this.items.forEach((item) => {
        const saved = savedItems[item.dataset.productHandle];
        if (!saved) return;

        const checkbox = item.querySelector('[data-bought-together-select]');
        if (checkbox && !checkbox.disabled && typeof saved.selected === 'boolean') {
          checkbox.checked = saved.selected;
        }

        const select = item.querySelector('[data-bought-together-variant]');
        const isCurrentProduct = item.dataset.currentProduct === 'true';

        if (select && !isCurrentProduct && saved.variantId) {
          const option = Array.from(select.options).find(
            (candidate) => candidate.value === String(saved.variantId) && !candidate.disabled,
          );

          if (option) {
            select.value = option.value;
            this.applyVariant(item, option, false);
          }
        }

        const input = item.querySelector('[data-bought-together-quantity]');
        if (input && !input.disabled && Number.isFinite(Number(saved.quantity))) {
          input.value = String(saved.quantity);
        }
      });
    }

    saveState() {
      if (!this.items?.length) return;

      const state = {};

      this.items.forEach((item) => {
        const input = item.querySelector('[data-bought-together-quantity]');
        const checkbox = item.querySelector('[data-bought-together-select]');
        state[item.dataset.productHandle] = {
          variantId: item.dataset.variantId || null,
          quantity: Number(input?.value) || 0,
          selected: checkbox ? checkbox.checked : true,
        };
      });

      this.stateStore.set(this.stateKey, state);
    }

    handleClick(event) {
      const minus = event.target.closest('[data-bought-together-minus]');
      const plus = event.target.closest('[data-bought-together-plus]');
      const button = minus || plus;

      if (!button || !this.contains(button)) return;

      const item = button.closest('[data-bought-together-item]');
      const input = item?.querySelector('[data-bought-together-quantity]');
      if (!input || input.disabled) return;

      event.preventDefault();
      this.stepQuantity(input, plus ? 1 : -1);
      this.updateTotals();
      this.saveState();
    }

    handleChange(event) {
      const checkbox = event.target.closest('[data-bought-together-select]');
      if (checkbox && this.contains(checkbox)) {
        const item = checkbox.closest('[data-bought-together-item]');
        if (item) this.setItemSelected(item, checkbox.checked, true);
        this.updateTotals();
        this.saveState();
        return;
      }

      const select = event.target.closest('[data-bought-together-variant]');
      if (select && this.contains(select)) {
        const item = select.closest('[data-bought-together-item]');
        const option = select.selectedOptions[0];
        if (item && option) this.applyVariant(item, option, true);
        return;
      }

      const input = event.target.closest('[data-bought-together-quantity]');
      if (!input || !this.contains(input)) return;

      this.normalizeQuantity(input);
      this.updateTotals();
      this.saveState();
    }

    handleInput(event) {
      const input = event.target.closest('[data-bought-together-quantity]');
      if (!input || !this.contains(input)) return;
      this.updateTotals();
    }

    handleSubmit(event) {
      event.preventDefault();
      if (this.loading) return;

      const items = this.getSelectedItems();
      if (items.length === 0) {
        this.setStatus(this.dataset.emptyMessage || 'Select at least one product.', true);
        return;
      }

      this.addSelectedItems(items);
    }

    applyVariant(item, option, preserveZero) {
      const input = item.querySelector('[data-bought-together-quantity]');
      const checkbox = item.querySelector('[data-bought-together-select]');
      const selected = checkbox ? checkbox.checked : true;
      const wasZero = Number(input?.value) <= 0;
      const available = option.dataset.available === 'true';
      const price = Number(option.dataset.price) || 0;
      const compareAtPrice = Number(option.dataset.compareAtPrice) || 0;
      const quantityMin = Math.max(1, Number(option.dataset.quantityMin) || 1);
      const quantityStep = Math.max(1, Number(option.dataset.quantityStep) || 1);
      const quantityMax = this.parseMaximum(option.dataset.quantityMax);

      item.dataset.variantId = option.value;
      item.dataset.unitPrice = String(price);
      item.dataset.compareAtPrice = String(compareAtPrice);
      item.dataset.available = String(available);
      item.dataset.quantityMin = String(quantityMin);
      item.dataset.quantityStep = String(quantityStep);
      item.dataset.quantityMax = quantityMax === null ? '' : String(quantityMax);
      item.classList.toggle('is-unavailable', !available);
      item.classList.toggle('is-unselected', !selected);
      item.dataset.selected = String(available && selected);

      if (checkbox) {
        checkbox.disabled = !available;
        if (!available) checkbox.checked = false;
      }

      if (input) {
        input.disabled = !available || !selected;
        input.step = String(quantityStep);
        input.min = String(quantityMin);

        if (quantityMax === null) {
          input.removeAttribute('max');
        } else {
          input.max = String(quantityMax);
        }

        if (!available || !selected) {
          input.value = '0';
        } else {
          input.value = String(quantityMin);
          this.normalizeQuantity(input);
        }
      }

      item.querySelectorAll('[data-bought-together-minus], [data-bought-together-plus]').forEach((button) => {
        button.disabled = !available || !selected;
      });

      this.updateItemImage(item, option);
      this.updateItemPrice(item);
      if (input) this.updateQuantityButtons(input);
      this.updateTotals();
      this.saveState();
    }

    setItemSelected(item, selected, restoreQuantity = true) {
      const checkbox = item.querySelector('[data-bought-together-select]');
      const input = item.querySelector('[data-bought-together-quantity]');
      const available = item.dataset.available === 'true';
      const isSelected = Boolean(selected && available);

      if (checkbox) {
        checkbox.checked = isSelected;
        checkbox.disabled = !available;
      }

      item.dataset.selected = String(isSelected);
      item.classList.toggle('is-unselected', !isSelected);

      if (input) {
        if (!isSelected) {
          const currentQuantity = Number(input.value) || 0;
          if (currentQuantity > 0) item.dataset.selectedQuantity = String(currentQuantity);
          input.value = '0';
          input.disabled = true;
        } else {
          input.disabled = false;

          if (restoreQuantity && Number(input.value) <= 0) {
            const restoredQuantity =
              Number(item.dataset.selectedQuantity) ||
              Number(item.dataset.defaultQuantity) ||
              Number(item.dataset.quantityMin) ||
              1;
            input.value = String(restoredQuantity);
          }

          this.normalizeQuantity(input);
        }

        this.updateQuantityButtons(input);
      }
    }

    updateItemImage(item, option) {
      const image = item.querySelector('.bought-together-item__image');
      if (!image || !option.dataset.image) return;

      image.src = option.dataset.image;

      if (option.dataset.imageSrcset) {
        image.srcset = option.dataset.imageSrcset;
      } else {
        image.removeAttribute('srcset');
      }

      if (option.dataset.imageWidth) image.width = Number(option.dataset.imageWidth);
      if (option.dataset.imageHeight) image.height = Number(option.dataset.imageHeight);
      if (option.dataset.imageAlt) image.alt = option.dataset.imageAlt;
    }

    updateItemPrice(item) {
      const price = Number(item.dataset.unitPrice) || 0;
      const compareAtPrice = Number(item.dataset.compareAtPrice) || 0;
      const priceElement = item.querySelector('[data-bought-together-item-price]');
      const compareElement = item.querySelector('[data-bought-together-item-compare]');
      const badgeElement = item.querySelector('[data-bought-together-item-badge]');
      const discounted = compareAtPrice > price;

      if (priceElement) priceElement.textContent = this.formatMoney(price);

      if (compareElement) {
        compareElement.textContent = this.formatMoney(compareAtPrice);
        compareElement.hidden = !discounted;
      }

      if (badgeElement) {
        badgeElement.textContent = discounted
          ? `-${Math.round(((compareAtPrice - price) * 100) / compareAtPrice)}%`
          : '';
        badgeElement.hidden = !discounted;
      }
    }

    stepQuantity(input, direction) {
      const item = input.closest('[data-bought-together-item]');
      const min = Math.max(1, Number(item?.dataset.quantityMin) || 1);
      const step = Math.max(1, Number(item?.dataset.quantityStep) || 1);
      const max = this.parseMaximum(item?.dataset.quantityMax);
      let value = Number(input.value) || 0;

      if (direction > 0) {
        value = value <= 0 ? min : value + step;
      } else {
        value = Math.max(min, value - step);
      }

      if (max !== null) value = Math.min(value, max);
      input.value = String(value);
      this.normalizeQuantity(input);
    }

    normalizeQuantity(input) {
      const item = input.closest('[data-bought-together-item]');
      if (!item || input.disabled) return 0;

      const min = Math.max(1, Number(item.dataset.quantityMin) || 1);
      const step = Math.max(1, Number(item.dataset.quantityStep) || 1);
      const max = this.parseMaximum(item.dataset.quantityMax);
      let value = Math.max(0, Math.floor(Number(input.value) || 0));

      value = Math.max(value, min);
      value = min + Math.floor((value - min) / step) * step;

      if (max !== null) value = Math.min(value, max);

      input.value = String(value);
      this.updateQuantityButtons(input);
      return value;
    }

    updateQuantityButtons(input) {
      const item = input.closest('[data-bought-together-item]');
      if (!item) return;

      const value = Number(input.value) || 0;
      const min = Math.max(1, Number(item.dataset.quantityMin) || 1);
      const max = this.parseMaximum(item.dataset.quantityMax);
      const minus = item.querySelector('[data-bought-together-minus]');
      const plus = item.querySelector('[data-bought-together-plus]');
      const available = item.dataset.available === 'true';
      const selected = item.dataset.selected !== 'false';

      if (minus) {
        minus.disabled = !available || !selected || value <= min;
      }

      if (plus) {
        plus.disabled = !available || !selected || (max !== null && value >= max);
      }
    }

    updateTotals() {
      let totalPrice = 0;
      let totalCompareAtPrice = 0;
      let selectedLines = 0;

      this.items.forEach((item) => {
        const input = item.querySelector('[data-bought-together-quantity]');
        if (!input) return;

        const quantity = Math.max(0, Math.floor(Number(input.value) || 0));
        const available = item.dataset.available === 'true';
        const selected = item.dataset.selected !== 'false';
        const price = Number(item.dataset.unitPrice) || 0;
        const compareAtPrice = Number(item.dataset.compareAtPrice) || 0;

        this.updateQuantityButtons(input);

        if (!available || !selected || quantity <= 0) return;

        selectedLines += 1;
        totalPrice += price * quantity;
        totalCompareAtPrice += Math.max(compareAtPrice, price) * quantity;
      });

      if (this.total) this.total.textContent = this.formatMoney(totalPrice);

      const hasSavings = totalCompareAtPrice > totalPrice;

      if (this.totalCompare) {
        this.totalCompare.textContent = this.formatMoney(totalCompareAtPrice);
        this.totalCompare.hidden = !hasSavings || this.dataset.showTotalCompare !== 'true';
      }

      if (this.totalBadge) {
        this.totalBadge.textContent = hasSavings
          ? `-${Math.round(((totalCompareAtPrice - totalPrice) * 100) / totalCompareAtPrice)}%`
          : '';
        this.totalBadge.hidden = !hasSavings || this.dataset.showTotalBadge !== 'true';
      }

      if (this.submitButton) {
        this.submitButton.disabled = this.loading || selectedLines === 0;
      }
    }

    getSelectedItems() {
      const groupedItems = new Map();

      this.items.forEach((item) => {
        if (item.dataset.available !== 'true' || item.dataset.selected === 'false') return;

        const variantId = Number(item.dataset.variantId);
        const input = item.querySelector('[data-bought-together-quantity]');
        const quantity = input ? this.normalizeQuantity(input) : 0;

        if (!variantId || quantity <= 0) return;

        groupedItems.set(variantId, (groupedItems.get(variantId) || 0) + quantity);
      });

      return Array.from(groupedItems, ([id, quantity]) => ({ id, quantity }));
    }

    async addSelectedItems(items) {
      this.setLoading(true);
      this.setStatus('');

      const payload = { items };
      const sections = this.getCartSectionIds();

      if (sections.length > 0) {
        payload.sections = sections.slice(0, 5);
        payload.sections_url = `${window.location.pathname}${window.location.search}`;
      }

      try {
        const response = await fetch(this.getCartAddUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(result.description || result.message || this.dataset.errorMessage);
        }

        if (!result.id && Array.isArray(result.items) && result.items[0]) {
          result.id = result.items[0].key || result.items[0].id || null;
        }

        this.renderCartSections(result);
        this.publishCartUpdate(result, items);
        this.openCartDrawer(result);
        this.setStatus(this.dataset.successMessage || 'Selected products were added to your cart.');
      } catch (error) {
        console.error('Bought together:', error);
        const fallback = this.dataset.errorMessage || 'The selected products could not be added.';
        const message = error.message && error.message !== fallback ? `${fallback} ${error.message}` : fallback;
        this.setStatus(message, true);
      } finally {
        this.setLoading(false);
        this.updateTotals();
      }
    }

    getCartAddUrl() {
      if (this.dataset.cartAddUrl) return this.dataset.cartAddUrl;
      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart/add.js`;
    }

    getCartSectionIds() {
      const sectionIds = new Set();
      const drawer = document.querySelector('cart-drawer');

      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        drawer.getSectionsToRender().forEach((section) => {
          const id = section.section || section.id;
          if (id) sectionIds.add(id);
        });
      }

      document.querySelectorAll('[data-cart-section-id]').forEach((element) => {
        if (element.dataset.cartSectionId) sectionIds.add(element.dataset.cartSectionId);
      });

      if (drawer) sectionIds.add('cart-drawer');
      if (document.getElementById('shopify-section-cart-icon-bubble')) {
        sectionIds.add('cart-icon-bubble');
      }

      return Array.from(sectionIds).slice(0, 5);
    }

    renderCartSections(result) {
      if (!result?.sections) return;

      const renderer = document.querySelector('cart-drawer, cart-notification');
      if (renderer && typeof renderer.renderContents === 'function') {
        renderer.renderContents(result);
        return;
      }

      Object.entries(result.sections).forEach(([sectionId, html]) => {
        if (!html) return;

        const currentSection = document.getElementById(`shopify-section-${sectionId}`);
        if (!currentSection) return;

        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const updatedSection = parsedDocument.getElementById(`shopify-section-${sectionId}`);
        if (!updatedSection) return;

        currentSection.replaceChildren(...Array.from(updatedSection.childNodes).map((node) => node.cloneNode(true)));
      });
    }

    publishCartUpdate(result, items) {
      const detail = {
        source: 'bought-together',
        response: result,
        items,
      };

      document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
      this.dispatchEvent(new CustomEvent('bought-together:added', { bubbles: true, detail }));
    }

    openCartDrawer(result) {
      if (this.dataset.openCart !== 'true') return;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      if (typeof drawer.open === 'function') {
        drawer.open();
      }
    }

    setLoading(loading) {
      this.loading = loading;
      this.setAttribute('aria-busy', String(loading));

      if (this.spinner) this.spinner.hidden = !loading;
      if (this.submitButton) this.submitButton.disabled = loading;

      this.items.forEach((item) => {
        item.querySelectorAll('select, input, button').forEach((control) => {
          if (loading) {
            control.dataset.boughtTogetherWasDisabled = String(control.disabled);
            control.disabled = true;
          } else if (control.dataset.boughtTogetherWasDisabled !== undefined) {
            control.disabled = control.dataset.boughtTogetherWasDisabled === 'true';
            delete control.dataset.boughtTogetherWasDisabled;
          }
        });
      });
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.classList.toggle('is-error', Boolean(message) && isError);
    }

    parseMaximum(value) {
      const maximum = Number(value);
      return Number.isFinite(maximum) && maximum > 0 ? maximum : null;
    }

    formatMoney(cents) {
      const format = this.moneyFormat || '${{amount}}';
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholder);
      if (!match) return String((Number(cents) || 0) / 100);

      const formats = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_space_separator: [2, ' ', ','],
        amount_no_decimals_with_space_separator: [0, ' ', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_no_decimals_with_apostrophe_separator: [0, "'", '.'],
      };

      const [precision, thousands, decimal] = formats[match[1]] || formats.amount;
      const value = this.formatWithDelimiters(Number(cents) || 0, precision, thousands, decimal);
      return format.replace(placeholder, value);
    }

    formatWithDelimiters(cents, precision, thousands, decimal) {
      const number = (cents / 100).toFixed(precision);
      const [integer, fraction] = number.split('.');
      const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${formattedInteger}${decimal}${fraction}` : formattedInteger;
    }
  }

  customElements.define('bought-together', BoughtTogether);
})();
